import { api } from './api'

/**
 * #270 — client de `GET /integrations` : quelles intégrations sont ACTIVES
 * pour l'organisation (AlloDons seule pour l'instant, la forme de liste
 * accueillera les cartes suivantes).
 *
 * Lu par la barre latérale et par toutes les gardes d'affichage (fiche,
 * numéro inconnu, historique donateur, onglet Dons) : la requête est posée en
 * ARRIÈRE-PLAN (`X-Background-Request`) — un serveur d'avant le lot (404) ou
 * une coupure réseau ne doit JAMAIS toaster sur chaque écran ; l'écran se
 * contente de ne rien montrer d'AlloDons.
 */
export type IntegrationKey = 'allodons'
export type IntegrationStatus = 'not_connected' | 'connected' | 'needs_renewal'

/**
 * #253 — une association AlloDons de l'organisation (une ligne de
 * raccordement chacune). Aucun secret : la liste est lisible par les agents.
 */
export interface AllodonsAssociation {
  id: string
  /** Nom affiché (`label`, sinon le slug) ; `null` = l'écran dit « Association principale ». */
  label: string | null
  orgSlug: string | null
  isDefault: boolean
  /** Raccordée (jeton vérifié, webhook reçu ou rattrapage abouti). */
  enabled: boolean
  status: IntegrationStatus
  lastWebhookAt: string | null
  /** #245 — clé serveur du module de paiement enregistrée (saisie de don en appel possible). FACULTATIF : serveur d'avant #245. */
  hasPayServerKey?: boolean
  /** #245 — 4 derniers caractères de la clé ; `null` sans clé. */
  payServerKeyLast4?: string | null
}

export interface IntegrationState {
  key: IntegrationKey
  enabled: boolean
  status: IntegrationStatus
  /** AlloDons : slug de la page de collecte de l'association PAR DÉFAUT (`allodons.fr/<slug>`). */
  orgSlug?: string | null
  /** Organisation du CRM raccordée. */
  organizationName?: string | null
  lastWebhookAt?: string | null
  /**
   * #253 — toutes les associations, par défaut d'abord. FACULTATIF : un
   * serveur d'avant #253 ne le rend pas (registre §6, champ neuf) — l'écran
   * retombe alors sur l'association unique implicite.
   */
  associations?: AllodonsAssociation[]
  defaultAssociationId?: string | null
}

/** #253 — réponse de `GET /integrations/allodons/association`. */
export interface CampaignAssociation {
  association: AllodonsAssociation | null
  source: 'campaign' | 'default' | 'none'
}

const BACKGROUND_HEADERS = { 'X-Background-Request': 'true' } as const
const STATUSES: IntegrationStatus[] = ['not_connected', 'connected', 'needs_renewal']

const statusOf = (value: unknown): IntegrationStatus =>
  STATUSES.includes(value as IntegrationStatus) ? (value as IntegrationStatus) : 'not_connected'

const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null)

/** #253 — une association hors contrat (sans id) est écartée, jamais devinée. */
export function normalizeAssociation(entry: unknown): AllodonsAssociation | null {
  if (!entry || typeof entry !== 'object') return null
  const item = entry as Record<string, unknown>
  if (typeof item.id !== 'string' || !item.id) return null
  return {
    id: item.id,
    label: stringOrNull(item.label),
    orgSlug: stringOrNull(item.orgSlug),
    isDefault: item.isDefault === true,
    enabled: item.enabled === true,
    status: statusOf(item.status),
    lastWebhookAt: stringOrNull(item.lastWebhookAt),
    hasPayServerKey: item.hasPayServerKey === true,
    payServerKeyLast4: item.hasPayServerKey === true ? stringOrNull(item.payServerKeyLast4) : null,
  }
}

/**
 * #253 — réponse de la résolution campagne → association ; hors contrat ⇒
 * « aucune ». Une association rendue SANS être raccordée elle-même compte :
 * le serveur ne la rend que si l'organisation l'est, et son historique est en
 * base (Kolme : « Ner David », par défaut, sans jeton depuis août).
 */
export function normalizeCampaignAssociation(data: unknown): CampaignAssociation {
  const item = (data ?? {}) as Record<string, unknown>
  const association = normalizeAssociation(item.association)
  const source = item.source === 'campaign' || item.source === 'default' ? item.source : 'none'
  return association
    ? { association, source: source === 'none' ? 'default' : source }
    : { association: null, source: 'none' }
}

/**
 * Hors contrat ⇒ liste vide (registre §5, #193 : un composant de l'AppShell
 * qui lit une réponse mal formée fait tomber l'écran entier). Une entrée sans
 * `enabled: true` STRICT n'active rien.
 */
export function normalizeIntegrations(data: unknown): IntegrationState[] {
  const list = (data as { integrations?: unknown } | null | undefined)?.integrations
  if (!Array.isArray(list)) return []
  return list.flatMap((entry): IntegrationState[] => {
    if (!entry || typeof entry !== 'object') return []
    const item = entry as Record<string, unknown>
    if (item.key !== 'allodons') return []
    const status = statusOf(item.status)
    const associations = Array.isArray(item.associations)
      ? item.associations.flatMap((entry) => {
          const association = normalizeAssociation(entry)
          return association ? [association] : []
        })
      : undefined
    return [
      {
        key: 'allodons',
        enabled: item.enabled === true,
        status,
        orgSlug: typeof item.orgSlug === 'string' ? item.orgSlug : null,
        organizationName: typeof item.organizationName === 'string' ? item.organizationName : null,
        lastWebhookAt: typeof item.lastWebhookAt === 'string' ? item.lastWebhookAt : null,
        ...(associations ? { associations } : {}),
        ...(typeof item.defaultAssociationId === 'string'
          ? { defaultAssociationId: item.defaultAssociationId }
          : {}),
      },
    ]
  })
}

const IntegrationsService = {
  /** `tenantId` : compte système seulement (sélecteur d'organisation). */
  list: async (tenantId?: string): Promise<IntegrationState[]> => {
    const { data } = await api.get('/integrations', {
      headers: BACKGROUND_HEADERS,
      ...(tenantId ? { params: { tenantId } } : {}),
    })
    return normalizeIntegrations(data)
  },

  /**
   * #253 — l'association AlloDons d'un appel : celle de la campagne, sinon
   * (sans campagne) l'association par défaut. `association: null` = pas de
   * collecte (onglet Dons absent). Requête de FOND, comme `list`.
   */
  campaignAssociation: async (campaignId?: string | null): Promise<CampaignAssociation> => {
    const { data } = await api.get('/integrations/allodons/association', {
      headers: BACKGROUND_HEADERS,
      ...(campaignId ? { params: { campaignId } } : {}),
    })
    return normalizeCampaignAssociation(data)
  },
}

export default IntegrationsService
